// The one place that decides what text a failed request puts on screen.
//
// SHCT-702. Every feature had its own copy of this rule, written as
//
//     t(`errors.${code}`, { defaultValue: err.message })
//
// which reads as "prefer our copy, fall back to the server's". The fallback is the defect: when the
// server sends a code the screen does not model, there IS no key, so `defaultValue` wins and the
// server's own text — an internal exception string, in whatever language the server happens to speak
// — is what the operator reads. That is exactly how a 500 on 약관 관리 surfaced its raw message
// verbatim: the backend answers a server fault with `INTERNAL_ERROR`, and each feature's guard listed
// only `UNKNOWN`/`NETWORK`, so the new code walked straight past it.
//
// The guard cannot be a list of codes to suppress. Suppression lists are written against the codes
// that exist today and are silently wrong the moment the backend adds one — which is the whole story
// of this bug. So the rule is inverted here: a message is shown ONLY when this app has copy for it.
// Anything else falls back to the caller's operation-specific line ("삭제에 실패했습니다…"), which is
// always true, always translated, and never leaks server internals.

import type { TFunction } from 'i18next';

/**
 * Codes that carry no business meaning, whose text is therefore never worth showing even when a key
 * happens to exist for them.
 *
 * `errors.UNKNOWN` resolves to "알 수 없는 오류가 발생했습니다." in several bundles — strictly less useful
 * than the caller's "콘텐츠 삭제에 실패했습니다.", which at least says WHAT failed. So these skip the key
 * lookup entirely and go straight to the operation fallback.
 */
const GENERIC_CODES: ReadonlySet<string> = new Set([
  'UNKNOWN',
  'NETWORK',
  'INTERNAL_ERROR',
  'TRANSACTION_FAILED',
]);

export interface ResolveErrorMessageOptions {
  /**
   * Namespace prefix in front of the code, defaulting to the `errors.` most feature bundles use.
   * Pass the fully-qualified prefix where the bundle nests them (`adminManagement.errors.`).
   */
  keyPrefix?: string;
  /** Interpolation values for the resolved message (e.g. a conflicting range). */
  values?: Record<string, unknown>;
  /**
   * Codes whose SERVER text may be shown when this app has no copy of its own.
   *
   * Only safe where the adapter allowlists codes at the wire boundary, so that an unmodelled code can
   * never reach here carrying its raw text (see `admins/api/admin.api.ts`, which collapses anything
   * unrecognised to `UNKNOWN` and drops the message with it). Without that guarantee, leave it unset.
   */
  allowServerMessageFor?: ReadonlySet<string>;
}

/**
 * The message to show for `code`, or the caller's operation-specific fallback.
 *
 * @param code the error code, or null when the failure is not a typed API error at all.
 * @param fallbackKey i18n key naming what the user was doing ("삭제에 실패했습니다…"), used whenever this
 *                    app has nothing better. Callers should always pass one.
 * @param serverMessage the server's own text — used only for a code in `allowServerMessageFor`.
 */
export function resolveApiErrorMessage(
  t: TFunction,
  code: string | null | undefined,
  fallbackKey: string,
  serverMessage?: string,
  options?: ResolveErrorMessageOptions,
): string {
  if (code && !GENERIC_CODES.has(code)) {
    const key = `${options?.keyPrefix ?? 'errors.'}${code}`;
    // An empty defaultValue is the sentinel for "no copy here" — i18next returns the key itself when
    // asked for a missing key, which would put `errors.INTERNAL_ERROR` on screen.
    const translated = t(key, { ...options?.values, defaultValue: '' });
    if (translated) return translated;
    if (options?.allowServerMessageFor?.has(code) && serverMessage) {
      return serverMessage;
    }
  }
  return t(fallbackKey, options?.values);
}
